import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Eye, ArrowLeft, Check, X, Users, User,
  RefreshCw, Clock, UserCheck, ChevronDown, ChevronUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getToken() {
  return localStorage.getItem("admin_token") || "";
}
function authHeader() {
  return { Authorization: `Bearer ${getToken()}` };
}

export default function AdminPengawas() {
  const navigate = useNavigate();
  const [pengawasList, setPengawasList] = useState([]);
  const [petugasList, setPetugasList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [assigningFor, setAssigningFor] = useState(null); // pengawas id being assigned
  const [selectedPetugas, setSelectedPetugas] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedPengawas, setExpandedPengawas] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [pengawasRes, petugasRes] = await Promise.all([
        axios.get(`${API}/admin/pengawas`, { headers: authHeader() }),
        axios.get(`${API}/admin/petugas-list`, { headers: authHeader() })
      ]);
      setPengawasList(pengawasRes.data);
      setPetugasList(petugasRes.data);
    } catch (err) {
      // Do NOT auto-navigate on 401. Admin only logs out via "Keluar" button.
      if (err.response?.status === 401) {
        toast.error("Gagal memuat data (unauthorized).");
        return;
      }
      toast.error("Gagal memuat data");
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) { navigate("/admin/login"); return; }
    fetchData();
  }, [fetchData, navigate]);

  const handleStartAssign = (pengawas) => {
    setAssigningFor(pengawas);
    setSelectedPetugas(pengawas.assigned_petugas || []);
  };

  const handleTogglePetugas = (petugasId) => {
    setSelectedPetugas(prev => 
      prev.includes(petugasId) 
        ? prev.filter(id => id !== petugasId)
        : [...prev, petugasId]
    );
  };

  const handleSaveAssignment = async () => {
    if (!assigningFor) return;
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/admin/pengawas/${assigningFor.id}/assign`, {
        petugas_ids: selectedPetugas
      }, { headers: authHeader() });
      
      toast.success("Berhasil menyimpan assignment petugas");
      setAssigningFor(null);
      setSelectedPetugas([]);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusBadge = (status) => {
    const styles = {
      pending: "bg-amber-100 text-amber-700",
      active: "bg-emerald-100 text-emerald-700",
      suspended: "bg-red-100 text-red-700"
    };
    const labels = { pending: "Menunggu", active: "Aktif", suspended: "Nonaktif" };
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${styles[status] || "bg-slate-100 text-slate-600"}`}>
        {labels[status] || status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin/dashboard">
              <Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4" /></Button>
            </Link>
            <div className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-purple-600" />
              <h1 className="font-heading font-semibold text-lg text-slate-800">Kelola Pengawas</h1>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchData}><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-3xl">
        {isLoading ? (
          <div className="text-center py-12 text-slate-500">Memuat...</div>
        ) : pengawasList.length === 0 ? (
          <div className="text-center py-12">
            <Eye className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Belum Ada Pengawas</h3>
            <p className="text-slate-500">Pengawas akan muncul di sini setelah mendaftar dan disetujui</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pengawasList.map((pengawas) => (
              <motion.div key={pengawas.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Card className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-slate-800">{pengawas.username}</p>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">Pengawas</span>
                          {statusBadge(pengawas.status)}
                        </div>
                        <p className="text-sm text-slate-500">{pengawas.phone}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          Daftar: {new Date(pengawas.created_at).toLocaleDateString("id-ID")}
                        </p>
                      </div>
                    </div>

                    {/* Assigned Petugas Info */}
                    <div className="mb-3">
                      <button
                        onClick={() => setExpandedPengawas(expandedPengawas === pengawas.id ? null : pengawas.id)}
                        className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800"
                      >
                        <Users className="w-4 h-4" />
                        <span>{pengawas.assigned_petugas_info?.length || 0} Petugas di-assign</span>
                        {expandedPengawas === pengawas.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      
                      {expandedPengawas === pengawas.id && pengawas.assigned_petugas_info?.length > 0 && (
                        <div className="mt-2 pl-6 space-y-1">
                          {pengawas.assigned_petugas_info.map(p => (
                            <div key={p.id} className="text-xs text-slate-600 flex items-center gap-2">
                              <User className="w-3 h-3" />
                              {p.username} ({p.phone})
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 flex-wrap">
                      {pengawas.status === "active" && (
                        <Button 
                          size="sm" 
                          className="rounded-full bg-purple-600 hover:bg-purple-700 text-xs gap-1"
                          onClick={() => handleStartAssign(pengawas)}
                          data-testid={`assign-${pengawas.id}`}
                        >
                          <Users className="w-3.5 h-3.5" /> Atur Petugas
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Assign Petugas Modal */}
      {assigningFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setAssigningFor(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-heading font-semibold text-lg text-slate-800 mb-1">
              Assign Petugas ke {assigningFor.username}
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              Pilih petugas yang dapat diakses laporan-nya oleh pengawas ini
            </p>
            
            {petugasList.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">Belum ada petugas aktif</p>
              </div>
            ) : (
              <div className="space-y-2 mb-4">
                {petugasList.map(petugas => (
                  <button
                    key={petugas.id}
                    onClick={() => handleTogglePetugas(petugas.id)}
                    className={`w-full p-3 rounded-xl border-2 flex items-center gap-3 transition-all ${
                      selectedPetugas.includes(petugas.id)
                        ? "border-purple-500 bg-purple-50"
                        : "border-slate-200 bg-white hover:border-purple-300"
                    }`}
                    data-testid={`petugas-${petugas.id}`}
                  >
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedPetugas.includes(petugas.id) 
                        ? "border-purple-500 bg-purple-500" 
                        : "border-slate-300"
                    }`}>
                      {selectedPetugas.includes(petugas.id) && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-slate-800">{petugas.username}</p>
                      <p className="text-xs text-slate-500">{petugas.phone}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1 rounded-full" onClick={() => setAssigningFor(null)}>
                Batal
              </Button>
              <Button
                className="flex-1 rounded-full bg-purple-600 hover:bg-purple-700"
                onClick={handleSaveAssignment}
                disabled={isSubmitting}
                data-testid="save-assignment-btn"
              >
                {isSubmitting ? <div className="spinner w-5 h-5" /> : "Simpan"}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
